import React from "react";
import {QRCodeCanvas} from 'qrcode.react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import LocationInfo from "models/LocationInfo";
import AuthContext from "../store/AuthContext";

const QRCodeBlock: React.FC<{dimId: string, locations?: LocationInfo[]}> = (props) => {
    const authCtx = React.useContext(AuthContext);
    const [imageSize, setImageSize] = React.useState(256);
    const [liveView, setLiveView] = React.useState(false);

    const [forcePlacement, setForcePlacement] = React.useState(false);

    const [imageSizeGenerate, setImageSizeGenerate] = React.useState(imageSize);
    const [imageValid, setImageValid] = React.useState(false);
    const [qrValue, setQrValue] = React.useState('');
    const [route, setRoute] = React.useState('');
    const [routeTab, setRouteTab] = React.useState('');
    const [selectedLocId, setSelectedLocId] = React.useState('');
    const [customKey, setCustomKey] = React.useState('');
    const [customValue, setCustomValue] = React.useState('');
    const [customParams, setCustomParams] = React.useState<{ key: string, value: string }[]>([]);
    const [bgColor, setBgColor] = React.useState("#ffffff");
    const [fgColor, setFgColor] = React.useState("#00134d");
    const [iconMode, setIconMode] = React.useState<'dimension' | 'dimx' | 'none'>('dimension');

    React.useEffect(() => {
        setQrValue(makeCodeValue());
    }, [props.dimId, selectedLocId, liveView, forcePlacement, route, routeTab, customParams]);

    function qrSizeChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        //setImageSize(Math.trunc(+e.target.value));
        setImageSize(parseInt(e.target.value));
    }

    function generateQRHandler() {
        if (imageSize >= 128 && imageSize <= 2048) {
            setImageSizeGenerate(imageSize);
            setImageValid(true);
        } else {
            setImageValid(false);
            window.alert("Invalid image size");
        }
    }

    function makeCodeValue() {
        const params = new URLSearchParams();
        params.set('dim', props.dimId);

        if (selectedLocId) {
            params.set('loc', selectedLocId);
        }
        if (liveView) {
            params.set('live', '1');
        }
        if (forcePlacement) {
            params.set('place', '1');
        }
        if (route) {
            params.set('route', route);
        }
        if (routeTab) {
            params.set('route_tab', routeTab);
        }
        if (customParams.length > 0) {
            const customStr = customParams.map(p => `${p.key}:${p.value}`).join(',');
            params.set('custom', customStr);
        }

        return `https://go.dimx.world/?${params.toString()}`;
    }

    function liveViewChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setLiveView(e.target.checked);
    }

    function forcePlacementChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setForcePlacement(e.target.checked);
    }

    function addCustomParam() {
        if (customKey && customValue) {
            setCustomParams(prev => [...prev, { key: customKey, value: customValue }]);
            setCustomKey('');
            setCustomValue('');
        }
    }

    function removeCustomParam(index: number) {
        setCustomParams(prev => prev.filter((_, i) => i !== index));
    }

    return (
        <>
            <Form.Group controlId="qr-code-route-fields" style={{ marginBottom: 'var(--margin)' }}>
                <Row>
                    <Col>
                        <Form.Label>Route name:</Form.Label>
                        <Form.Control type="text" value={route} onChange={(e) => setRoute(e.target.value)}
                            placeholder="Enter route name" />
                    </Col>
                    <Col>
                        <Form.Label>Route tab:</Form.Label>
                        <Form.Control type="text" value={routeTab} onChange={(e) => setRouteTab(e.target.value)}
                            placeholder="Enter route tab" />
                    </Col>
                </Row>
            </Form.Group>

            <Form.Group controlId="qr-code-locations" style={{ marginBottom: 'var(--margin)' }}>
                <Form.Label>Location</Form.Label>
                <Form.Select value={selectedLocId} onChange={(e) => setSelectedLocId(e.target.value)}>
                    <option value="">-- None --</option>
                    {props.locations
                        ?.slice() 
                        .sort((a, b) => a.name.localeCompare(b.name)) 
                        .map((loc: LocationInfo) => (
                            <option key={loc.id} value={loc.id}>
                                {loc.name}
                            </option>
                    ))}
                </Form.Select>
            </Form.Group>
      
            <div className="d-flex gap-3 mt-3">
                <Form.Group controlId="qr-code-section-live" style={{ marginBottom: "var(--margin)"}}>
                    <Form.Check 
                        type="checkbox" 
                        label="Live view" 
                        checked={liveView} onChange={liveViewChangeHandler}
                    />
                </Form.Group>
                <Form.Group controlId="qr-code-section-force-place" style={{ marginBottom: "var(--margin)"}}>
                    <Form.Check 
                        type="checkbox" 
                        label="Force placement" 
                        checked={forcePlacement} onChange={forcePlacementChangeHandler}
                    />
                </Form.Group>
            </div>
            
            <Form.Group controlId="qr-code-custom" style={{ marginBottom: 'var(--margin)' }}>
                <Form.Label>Custom Parameters</Form.Label>
                <Row className="align-items-center mb-2">
                    <Col>
                        <Form.Control type="text" value={customKey} onChange={(e) => setCustomKey(e.target.value)}
                            placeholder="Key" />
                    </Col>
                    <Col>
                        <Form.Control type="text" value={customValue} onChange={(e) => setCustomValue(e.target.value)}
                            placeholder="Value" />
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={addCustomParam}>+</Button>
                    </Col>
                </Row>

                {customParams.length > 0 && (
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Value</th>
                                <th style={{ width: '1%', whiteSpace: 'nowrap' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {customParams.map((param, index) => (
                                <tr key={index}>
                                    <td>{param.key}</td>
                                    <td>{param.value}</td>
                                    <td>
                                        <Button variant="danger" size="sm" onClick={() => removeCustomParam(index)}>-</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Form.Group>
            
            <Form.Group controlId="qr-code-section-preview" style={{ marginBottom: "var(--margin)"}}>
                <Form.Label>Encoded URL</Form.Label>
                <Form.Control as="textarea" rows={6} readOnly value={qrValue} />
            </Form.Group>

            <Form.Group controlId="qr-code-section-size" className="d-flex align-items-center gap-2 mt-3" style={{ marginBottom: "var(--margin)" }}>
                <Form.Label className="mb-0" style={{ width: '120px'}} >Image Size (px)</Form.Label>
                <Form.Control type="number" value={imageSize} onChange={qrSizeChangeHandler} />
            </Form.Group>
            <Form.Group controlId="qr-code-colors" className="mb-3 mt-3">
                <div className="d-flex align-items-center gap-4">
                    <div className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0">Background</Form.Label>
                        <Form.Control
                            type="color"
                            value={bgColor}
                            onChange={(e) => setBgColor(e.target.value)}
                            style={{ width: '60px' }}
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0">Foreground</Form.Label>
                        <Form.Control
                            type="color"
                            value={fgColor}
                            onChange={(e) => setFgColor(e.target.value)}
                            style={{ width: '60px' }}
                        />
                    </div>
                </div>
            </Form.Group>

            <Form.Group controlId="qr-code-icon-toggle" className="mb-3">
                <div className="d-flex gap-3">
                    <Form.Check
                        type="radio"
                        id="icon-dimension"
                        name="qr-icon"
                        label="Dimension Icon"
                        checked={iconMode === 'dimension'}
                        onChange={() => setIconMode('dimension')}
                    />
                    <Form.Check
                        type="radio"
                        id="icon-dimx"
                        name="qr-icon"
                        label="DimX Icon"
                        checked={iconMode === 'dimx'}
                        onChange={() => setIconMode('dimx')}
                    />
                    <Form.Check
                        type="radio"
                        id="icon-none"
                        name="qr-icon"
                        label="No Icon"
                        checked={iconMode === 'none'}
                        onChange={() => setIconMode('none')}
                    />
                </div>
            </Form.Group>
            
            <Button variant="primary" onClick={generateQRHandler}>Generate QR Code</Button>
            
            {imageValid && (
                <div className="qr">
                    <QRCodeCanvas
                        value={makeCodeValue()}
                        size={imageSizeGenerate}
                        bgColor={bgColor}
                        fgColor={fgColor}
                        level={"H"}
                        includeMargin={true}
                        {...(iconMode !== 'none' && {
                            imageSettings: {
                                src: iconMode === 'dimension'
                                    ? authCtx.makeFileUrl(props.dimId, 'icon.png')
                                    : 'qrcode-logo.png',
                                height: imageSizeGenerate / 4,
                                width: imageSizeGenerate / 4,
                                excavate: true,
                            }
                        })}
                    />
                </div>
            )}
        </>
    );
};

export default QRCodeBlock;
